import { orderBy } from 'es-toolkit';

import { COMMUNICATION_TASK_TITLE_PREFIX, COMMUNICATION_TASK_TYPE_NAME } from '@/constants/communication.ts';

import { GenericPatient } from '@/models/GenericPatient.ts';
import { TaskSortByOption } from '@/models/SortByOption.ts';
import { TaskPrefill } from '@/models/TaskPrefill.ts';
import { Appointment } from '@/models/appointment/Appointment.ts';
import { AppointmentBookingFormValues } from '@/models/appointment/AppointmentBookingFormValues.ts';
import { EnrichedPractitionerData } from '@/models/appointment/EnrichedPractitionerData.ts';
import { DataCurPracUser } from '@/models/data-cur/DataCurPracUser.ts';
import { DCCalendar } from '@/models/doc-cirrus/DCCalendar.ts';
import { DCTask, DCTaskStatus, EnrichedTask } from '@/models/doc-cirrus/DCTask.ts';
import { DCTaskTypeInternal } from '@/models/doc-cirrus/DCTaskTypes.ts';
import { DCUser } from '@/models/doc-cirrus/DCUser.ts';

import type { EnrichedAppointmentWithBookingStatus } from '@/stores/bookingStore.ts';

import { getInstanceId, getPvsPatientId } from '@/utils/appointmentUtils.ts';
import { getDateOfBirthSearchValues } from '@/utils/date-utils.ts';
import { MISSING_PATIENT_ID } from '@/utils/patientUtils.ts';

export const ROLE_NONE = '__keine_rolle__';
export const PRACTICE_NONE = '__keine_praxis__';

interface BuildBookingTaskPrefillParams {
    bookedAppointments: EnrichedAppointmentWithBookingStatus[];
    currentPatient: GenericPatient | undefined;
    practitionerData: EnrichedPractitionerData | undefined;
    currentForm: Pick<AppointmentBookingFormValues, 'practice_doc_cirrus_id'> | undefined;
    instanceId: string;
}

/** Only emits a location when both id and name are present, to avoid an `id::undefined` option. */
const toTaskLocations = (
    practitionerData: EnrichedPractitionerData | undefined,
    practiceId: string | undefined
): string[] => {
    const practice = practitionerData?.practices?.find((p) => p.pvs?.id === practiceId);
    return practice?.pvs?.id && practice.pvs.name ? [`${practice.pvs.id}::${practice.pvs.name}`] : [];
};

/** Location comes from the LAST booked appointment; schedule linked only when there is exactly one. */
export const buildBookingTaskPrefill = ({
    bookedAppointments,
    currentPatient,
    practitionerData,
    currentForm,
    instanceId,
}: BuildBookingTaskPrefillParams): TaskPrefill => {
    const lastBooked = bookedAppointments.at(-1);
    const practiceId = lastBooked?.form_data.practice_doc_cirrus_id || currentForm?.practice_doc_cirrus_id || '';
    const locations = toTaskLocations(practitionerData, practiceId);

    const linkable = bookedAppointments.filter((apt) => apt.appointment?.id);
    const scheduleId = linkable.length === 1 ? linkable[0].appointment?.id : undefined;

    return {
        instance_id: instanceId,
        patient_id: currentPatient?.pvs_user_id,
        patient_name: currentPatient
            ? `${currentPatient.personal.last_name} ${currentPatient.personal.first_name}`
            : undefined,
        locations: locations,
        schedule_id: scheduleId,
    };
};

export const buildPatientTaskPrefill = (patient: GenericPatient): TaskPrefill => ({
    instance_id: patient.instance_id,
    patient_id: patient.pvs_user_id,
    patient_name: `${patient.personal.last_name} ${patient.personal.first_name}`,
});

/** For a task raised from an existing appointment, where the Betriebsstätte is the location of its calendar. */
export const buildAppointmentTaskPrefill = (
    appointment: Appointment,
    practitionerData: EnrichedPractitionerData | undefined,
    patientName: string
): TaskPrefill => {
    const patientId = getPvsPatientId(appointment);
    const calendar = practitionerData?.practitioners
        .flatMap((practitioner) => practitioner.calendars)
        .find((entry) => entry.pvs.id === appointment.links?.calendar_id);
    const locationId = (calendar?.pvs as DCCalendar | undefined)?.location_id || appointment.links?.location_id;

    return {
        instance_id: getInstanceId(appointment),
        patient_id: patientId === MISSING_PATIENT_ID ? undefined : patientId,
        patient_name: patientName,
        locations: toTaskLocations(practitionerData, locationId),
        schedule_id: appointment.links?.schedule_id,
    };
};

/**
 * Turns a saved communication into a task. Title, type and details are fixed; the rest (patient,
 * Betriebsstätte, instance) comes from whichever prefill the entry point already builds.
 */
export const withCommunicationTaskFields = (base: TaskPrefill, content: string): TaskPrefill => ({
    ...base,
    title: `${COMMUNICATION_TASK_TITLE_PREFIX} ${base.patient_name ?? ''}`.trim(),
    details: content,
    task_type_name: COMMUNICATION_TASK_TYPE_NAME,
});

export const getPatientTasks = (
    tasks: EnrichedTask[],
    patient: Pick<GenericPatient, 'pvs_user_id' | 'instance_id'>
): EnrichedTask[] =>
    tasks.filter(
        (task) =>
            task.instance_id === patient.instance_id &&
            task.patient_id === patient.pvs_user_id &&
            task.status !== DCTaskStatus.DONE
    );

export const enrichTask = (
    task: DCTask,
    task_types: DCTaskTypeInternal[],
    instance_users_map: Record<string, DCUser | DataCurPracUser> | undefined
): EnrichedTask => {
    let task_type = task_types.find((type) => type.id === task.task_type && type.instance_id === task.instance_id);
    // Default to Standard task type if no task type is found
    if (!task_type) {
        task_type = task_types.find((type) => type.instance_id === task.instance_id && type.name === 'Standard');
    }

    return {
        ...task,
        creator: instance_users_map?.[`${task.instance_id}_${task.creator_id}`] as DCUser,
        type_name: task_type?.name,
    };
};

export const enrichTasks = (
    tasks: DCTask[],
    task_types: DCTaskTypeInternal[],
    instance_users_map: Record<string, DCUser | DataCurPracUser> | undefined
): EnrichedTask[] => {
    return tasks.map((task) => enrichTask(task, task_types, instance_users_map));
};

export const sortTasks = (tasks: EnrichedTask[], sort_by: TaskSortByOption): EnrichedTask[] => {
    switch (sort_by) {
        case TaskSortByOption.CREATED_ASC: {
            return orderBy(tasks, [(task): string => task.meta.created_at], ['asc']);
        }
        case TaskSortByOption.CREATED_DESC: {
            return orderBy(tasks, [(task): string => task.meta.created_at], ['desc']);
        }
        case TaskSortByOption.TARGET_DATE_ASC: {
            return orderBy(tasks, [(task): string => task.alert_time ?? ''], ['asc']);
        }
        case TaskSortByOption.TARGET_DATE_DESC: {
            return orderBy(tasks, [(task): string => task.alert_time ?? ''], ['desc']);
        }
        case TaskSortByOption.URGENCY_ASC: {
            return orderBy(tasks, [(task): number => task.urgency ?? 0], ['asc']);
        }
        case TaskSortByOption.URGENCY_DESC: {
            return orderBy(tasks, [(task): number => task.urgency ?? 0], ['desc']);
        }
        default: {
            return tasks;
        }
    }
};

/**
 * Approximates the nouns in a German text by its capitalized words. German capitalizes every noun,
 * so this is a cheap, dependency-free heuristic — the only systematic false positives are
 * sentence-initial words, which are harmless for search. Used to restrict task description search to
 * nouns (ET-5459). Matching against the result is still substring-based, so partial input and German
 * compounds keep working (e.g. "versicherung" -> "Krankenversicherung").
 */
export const extractGermanNouns = (text: string): string[] => text.match(/\p{Lu}[\p{L}\p{N}-]*/gu) ?? [];

export const filterTasks = (
    tasks: EnrichedTask[],
    roles: string[],
    practices: string[],
    only_me: boolean,
    auth_user_pvs_ids: Set<string>,
    search_text: string
): EnrichedTask[] => {
    // Filter
    let filtered_tasks = tasks
        .filter((task) => {
            // Role-less tasks are an explicit bucket, visible only when ROLE_NONE is selected
            if (!task.roles?.length) return roles.includes(ROLE_NONE);
            return task.roles.some((role) => roles.includes(role));
        })
        .filter((task) => {
            // Practice-less tasks are an explicit bucket, visible only when PRACTICE_NONE is selected
            if (!task.locations?.length) return practices.includes(PRACTICE_NONE);
            return task.locations.some((location) => practices.includes(location.id));
        });

    if (only_me) {
        // "My tasks": tasks I am assigned to (employee_id) or tasks I created (creator_id)
        filtered_tasks = filtered_tasks.filter(
            (task) =>
                (task.employee_id && auth_user_pvs_ids.has(task.employee_id)) ||
                (task.creator_id && auth_user_pvs_ids.has(task.creator_id))
        );
    }

    // Search: multi-word, case-insensitive and whitespace-tolerant. Each search word must match at
    // least one of the task's searchable fields, so users can combine words across fields — e.g. a
    // patient name plus a noun from the description. The description is searched by its nouns only
    // (approximated by capitalized words, see extractGermanNouns); the other fields match on
    // substring. Matching everywhere is substring-based, so partial input still works.
    const searchWords = search_text.toLowerCase().split(/\s+/).filter(Boolean);
    if (searchWords.length > 0) {
        filtered_tasks = filtered_tasks.filter((task) => {
            const descriptionNouns = task.details ? extractGermanNouns(task.details) : [];
            const searchableFields = [
                task.title,
                ...descriptionNouns,
                task.patient_name,
                task.patient_number,
                ...getDateOfBirthSearchValues(task.patient_date_of_birth),
            ]
                .filter((field): field is string => !!field)
                .map((field) => field.toLowerCase());

            return searchWords.every((word) => searchableFields.some((field) => field.includes(word)));
        });
    }

    return filtered_tasks;
};
